// MN-4845: shared helpers and constants for the guest DTF transfer-credit
// teaser experiment. Centralized here so the component, both PDP render
// surfaces, and the analytics layer share a single source of truth and so
// module-scoped dedup flags collapse across dual mounts.

import { submitDtfEvent } from "common/utils/mixpanel/submit-dtf-events";

export const GUEST_TEASER_EXPERIMENT_NAME = "ab_mn4845_guest_transfer_credit_teaser";
export const GUEST_TEASER_SIGNUP_SOURCE = "dtf_pdp_teaser";
// Per Transfer Rewards T&C (https://www.jiffy.com/transfer-rewards-terms-conditions),
// $40 is the minimum qualifying cart subtotal. Display-only threshold; the
// server-side gate lives in `::Order::AwardTransfersStoreCredit::CREDIT_TIERS`.
export const GUEST_TEASER_MIN_SUBTOTAL = 40;
// Defensive cap on the artwork payload we stash for rehydration. Real sessions
// rarely exceed 1-2 artworks; bound the state so a pathological upload loop
// can't bloat sessionStorage with megabytes of dangling presigned URLs.
export const GUEST_TEASER_MAX_ARTWORKS = 5;
// Storage key for the rehydration handoff. sessionStorage keeps the blob
// scoped to the current tab and auto-evicts on close.
const GUEST_TEASER_STORAGE_KEY = "guestDtfArtworkState";
// Stale threshold matches S3 presign default (1 hour). Any state older than
// this is discarded on read so a user who abandons signup then loads a PDP
// an hour later doesn't get a surprise rehydrate with expired URLs.
const GUEST_TEASER_STORAGE_TTL_MS = 60 * 60 * 1000;

interface StashedGuestArtworkState {
  uploadedArtworkIds: string[];
  artworkUrls: Record<string, string>;
  fileContentType: string | null;
  pdpPath: string;
  savedAt: number;
}

// Module-scoped flags so (a) the PDP base mount + MultiArtworkModal mount
// collapse into a single impression, and (b) re-renders from cart/subtotal
// changes don't double-count. Flags reset on full page load (module re-eval).
let eligibleEventFired = false;
let viewedEventFired = false;

// Exposed for tests so each test starts with a clean slate.
export function __resetGuestTeaserEventFlags() {
  eligibleEventFired = false;
  viewedEventFired = false;
}

// Fire `_eligible_guest` for BOTH experiment arms (Control=0 and V1=1) when a
// guest hits all teaser eligibility conditions. Kappa uses this as the
// symmetric denominator for signup-rate. Called from the parent PDP surfaces
// (ProductDetailsDtf.vue.tsx, MultiArtworkModal.vue.tsx) regardless of variant.
export function fireGuestTeaserEligibleEventIfNew(payload: {
  variant: number | undefined;
  totalCartAmount: number;
  expectedCredits: number;
  currentTier?: string | undefined;
  belowMinimum: boolean;
}) {
  if (eligibleEventFired) return;
  eligibleEventFired = true;
  submitDtfEvent("transfer_store_credit_teaser_eligible_guest", {
    variant: payload.variant ?? null,
    total_cart_amount: payload.totalCartAmount,
    expected_credits: payload.expectedCredits,
    current_tier: payload.currentTier,
    below_minimum: payload.belowMinimum
  });
}

// Fire `_viewed_guest` only when the teaser actually paints (V1 and all
// eligibility conditions met). Paired with `_eligible_guest` for a two-level
// funnel: eligible (both arms) -> viewed (V1 only) -> cta_clicked -> signed_up.
export function fireGuestTeaserViewedEventIfNew(payload: {
  totalCartAmount: number;
  expectedCredits: number;
  currentTier?: string | undefined;
  belowMinimum: boolean;
}) {
  if (viewedEventFired) return;
  viewedEventFired = true;
  submitDtfEvent("transfer_store_credit_teaser_viewed_guest", {
    total_cart_amount: payload.totalCartAmount,
    expected_credits: payload.expectedCredits,
    current_tier: payload.currentTier,
    below_minimum: payload.belowMinimum
  });
}

// Stash artwork state in sessionStorage so the PDP can rehydrate after
// signup. We deliberately do NOT put the S3 URLs (which carry signed AWS
// credentials) in the URL query string. sessionStorage is scoped to the tab,
// clears on close, and never rides along in browser history, Referrer
// headers, or server access logs.
//
// Also dodges a Devise redirect_to truncation issue where long query strings
// got stripped between POST /signup and the final redirect, which broke
// artwork rehydration on the previous URL-based implementation.
export function stashGuestArtworkState(input: {
  uploadedArtworkIds?: string[];
  artworkUrls?: Record<string, string>;
  fileContentType?: string | null;
  pdpPath?: string;
}): void {
  if (typeof window === "undefined" || !window.sessionStorage) return;

  const cappedIds = (input.uploadedArtworkIds ?? []).slice(0, GUEST_TEASER_MAX_ARTWORKS);
  if (cappedIds.length === 0) return;

  const cappedUrls: Record<string, string> = {};
  for (const id of cappedIds) {
    if (input.artworkUrls && input.artworkUrls[id]) {
      cappedUrls[id] = input.artworkUrls[id];
    }
  }

  const stashed: StashedGuestArtworkState = {
    uploadedArtworkIds: cappedIds,
    artworkUrls: cappedUrls,
    fileContentType: input.fileContentType ?? null,
    pdpPath: input.pdpPath ?? window.location.pathname,
    savedAt: Date.now()
  };

  try {
    window.sessionStorage.setItem(GUEST_TEASER_STORAGE_KEY, JSON.stringify(stashed));
  } catch {
    // Quota exceeded or storage blocked — silently degrade to no rehydration.
  }
}

// Reads the stash without clearing it. Returns null if absent, stale, or for
// a different PDP (defensive — shouldn't happen in the normal flow but a
// mismatch means the user drifted somewhere else before landing back here).
export function readGuestArtworkState(): StashedGuestArtworkState | null {
  if (typeof window === "undefined" || !window.sessionStorage) return null;

  let raw: string | null = null;
  try {
    raw = window.sessionStorage.getItem(GUEST_TEASER_STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;

  let parsed: StashedGuestArtworkState;
  try {
    parsed = JSON.parse(raw);
  } catch {
    clearGuestArtworkState();
    return null;
  }

  if (!parsed || !Array.isArray(parsed.uploadedArtworkIds)) {
    clearGuestArtworkState();
    return null;
  }

  if (Date.now() - (parsed.savedAt || 0) > GUEST_TEASER_STORAGE_TTL_MS) {
    clearGuestArtworkState();
    return null;
  }

  if (parsed.pdpPath && parsed.pdpPath !== window.location.pathname) {
    // User landed on a different PDP than where they uploaded — don't
    // mis-rehydrate a design onto the wrong product.
    return null;
  }

  return parsed;
}

export function clearGuestArtworkState(): void {
  if (typeof window === "undefined" || !window.sessionStorage) return;
  try {
    window.sessionStorage.removeItem(GUEST_TEASER_STORAGE_KEY);
  } catch {
    // ignore
  }
}

// Builds the `/signup` redirect URL. Stashes artwork state in sessionStorage
// so the PDP can rehydrate on return. `/signup` is the Jiffy-mounted Devise
// route (see config/solidus_auth_devise_routes_override.rb); the Devise
// default `/users/sign_up` returns 404 on this app.
export function buildSignupHref(input: {
  uploadedArtworkIds?: string[];
  artworkUrls?: Record<string, string>;
  fileContentType?: string | null;
}): string {
  if (typeof window === "undefined" || !window.location) {
    return `/signup?source=${GUEST_TEASER_SIGNUP_SOURCE}`;
  }

  stashGuestArtworkState({ ...input, pdpPath: window.location.pathname });

  const redirectTo = window.location.pathname + window.location.search;
  const signup = new URL("/signup", window.location.origin);
  signup.searchParams.set("source", GUEST_TEASER_SIGNUP_SOURCE);
  signup.searchParams.set("redirect_to", redirectTo);
  return signup.pathname + signup.search;
}

// Extracts { uploadedArtworkIds, artworkUrls } from an artwork list for the
// signup redirect URL. Filters out in-flight file uploads (which have no
// server id yet) and missing URLs.
export function collectArtworkStateForSignup(artworks: any[] | null | undefined): {
  uploadedArtworkIds: string[];
  artworkUrls: Record<string, string>;
} {
  const list: any[] = artworks || [];
  const ids: string[] = list
    .filter((a: any) => a && !a.isFile && a.id != null)
    .map((a: any) => String(a.id));
  const urls: Record<string, string> = list.reduce((acc: Record<string, string>, a: any) => {
    if (a && !a.isFile && a.id != null && a.url) {
      acc[String(a.id)] = a.url;
    }
    return acc;
  }, {});
  return { uploadedArtworkIds: ids, artworkUrls: urls };
}
